// The dedicated fifth screen: `client-rounds-review` (rounds-slice **Q5** / **D5**;
// ported from `prototype/sketches/rounds-slice/review-screen.html`).
//
// It is **not** a mode of the Match screen and it does not touch `client-replay` /
// `client-match`. The deciding argument is a capability difference, not taste: live
// replay works by re-simulation, so arbitrary seek is deferred (**D-123**) and
// `ReplayControls` keeps a deliberately read-only position strip; a batch trace is a
// **seekable file** (**A4**), so this screen's headline affordance is a draggable seek
// thumb. One component that is draggable-or-not depending on invisible provenance would
// be worse than two. This screen also never has a live subscription to degrade to, and
// carries run-comparison chrome that is meaningless on a live match.
//
// Composition:
//   * **screen identity** — kind / seed / colony count / duration, in persistent chrome,
//     because a sandbox run's seed and solo composition are exactly what make its score
//     non-comparable to a graded round's (**G6**, **R3**);
//   * **the field frame** — one tick of the trace, at whichever tier is available;
//   * **the score readout** — the **G1** metric as evidence, never a forecast (**R3**);
//   * **the run rail** — comparative diagnosis; switches the review target, and can do
//     nothing else (candidate selection is a successor change's);
//   * **the seekable transport** — drag-to-seek, pause/resume, ½×, and an mm:ss clock.
//
// Every seam is optional with an inert default, matching the other four routes: a bare
// mount with no run loaded renders the empty state rather than crashing.

import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  ReviewRun,
  TraceCorruptionReport,
  TraceLoad,
  TraceLocator,
  TraceReader,
} from '../../net/rounds-review-source';
import {
  DEFAULT_REVIEW_SPEED,
  clockLabel,
  runIdentityLine,
  runKindLabel,
  tickInsideSpans,
  tierStateFor,
} from '../rounds/review-model';
import {
  ReviewFrame,
  colonyFitZoom,
  fieldFitZoom,
  hiveCentreOf,
  type ReviewRenderer,
} from '../components/rounds/ReviewFrame';
import { ColonyChipBar, ColonyRail, type GradedColony } from '../components/rounds/ColonyRail';
import { eliminatedCountAt, spectateRailColonies } from '../rounds/spectate-model';
import { ScoreReadout } from '../components/rounds/ScoreReadout';
import { SeekTransport } from '../components/rounds/SeekTransport';
import { PhaseClock } from '../components/hud/PhaseClock';
import type { RoundsPhaseSnapshot } from '../../app/rounds/rounds-phase';
import {
  performanceClock,
  rafFrameScheduler,
  type Clock,
  type FrameScheduler,
} from '../../canvas/frame-scheduler';
import { COLONY_CLOSE_SCALE, screenToWorld } from '../../canvas/projection';
import { pickEntityAt } from '../../canvas/pick';
import { reviewWorldSnapshot } from '../rounds/review-world';
import {
  cameraForZoomCentre,
  panByScreenDelta,
  zoomAboutScreenPoint,
  type CameraGestureBounds,
} from '../camera/camera-gestures';
import {
  useCanvasCameraGestures,
  type CanvasCameraGestureHandlers,
} from '../camera/use-canvas-camera-gestures';
import { ReviewCameraControl } from '../components/rounds/ReviewCameraControl';
import { PickedBeeChip } from '../components/rounds/PickedBeeChip';
import { detailHoldTicks } from '../rounds/detail-continuity';

/** The seam bundle the shell injects into the review route. Optional everywhere, the
 * `inMatchDeps`/`colonyController` convention. */
export interface RoundsReviewSeams {
  /** The runs produced this round, in the order they were produced. */
  runs: readonly ReviewRun[];
  /** The trace-open seam (production: `rounds-http`; tests: the fixture-backed fake). */
  reader: TraceReader;
  /** Which run to open on mount. Defaults to the first run. */
  initialRunId?: string;
  /**
   * The world-renderer factory (**D-156**): the review frame draws through the shipped
   * PixiJS renderer, the same one the Colony and Match canvases use, so a run replays as
   * sprites rather than as marks. Optional with no default — a real renderer needs a GPU
   * context, so a bare mount and every jsdom test render the chrome with no canvas (the
   * `ColonyUnderlaySeams` precedent). Production supplies it from the composition root.
   */
  createRenderer?: (container: HTMLElement, tickRateHz: number) => ReviewRenderer;
  /** The graded review's one mode-carrying subject. Its locator remains fixed after open;
   * changing the watched colony is local screen state, never another trace request. */
  subject?: ReviewSubject;
  /** Ranked roster derived by the root from standings and the trace's participants. */
  colonies?: readonly GradedColony[];
  /** The one phase source shared with the sandbox screen; absent on an inert mount. */
  phase?: RoundsPhaseSnapshot;
  /** Optional route seam. The parent keeps this screen mounted across the standings round trip,
   * so calling it cannot reset this viewer's private playback state. */
  onOpenStandings?: () => void;
  /** Optional controlled snapshot saved above the route before `/standings` unmounts this view. */
  viewerState?: GradedReviewViewerState;
  /** Receives only private viewer state after a local seek, zoom, play/pause, or subject switch. */
  onViewerStateChange?: (state: GradedReviewViewerState) => void;
  /**
   * The **spectate** variant (`tournament-big-screen` design **D12**): a projected chrome over
   * the same trace reader, `ReviewFrame` and subject model — the default camera frames the whole
   * field rather than one colony's hive, the rail is bounded and ordered by score at the
   * playhead rather than by final placement, and an eliminated counter and a back-to-the-field
   * control are added. Never introduces a second renderer or a second trace open: every one of
   * those differences is a **reading** of state this screen already holds.
   *
   * `SpectateView` is the composable export that sets this; `RoundsReview` itself keeps its
   * ordinary player-facing chrome when it is absent.
   */
  spectate?: boolean;
}

/** The identity of a graded review. A locator is an address; `subjectColony` is the local
 * viewpoint inside the one trace that address returns. */
export interface GradedReviewSubject {
  mode: 'graded';
  locator: Extract<TraceLocator, { kind: 'gradedRound' }>;
  subjectColony: number;
  /** Round metadata already known at the route seam; trace metadata remains the authority when
   * supplied by the production composition. */
  roundsTotal?: number | null;
  seed?: number | null;
  colonyCount?: number | null;
}

/**
 * A graded review was asked for, but the caller has **no validated colony** to be the subject of it
 * (implementation-review **F4**).
 *
 * Its own variant rather than an absent `subject`, and emphatically not a `subjectColony` of `0`:
 * `0` is a real colony, and because a tournament credential may legitimately read other members'
 * graded traces, substituting it opens *that* colony's trace and labels it as the caller's own. An
 * absent `subject` is no better here — it reads as "this is a sandbox review", which is a different
 * screen. So the absence is carried as a value the screen can name.
 */
export interface GradedSubjectUnavailable {
  mode: 'gradedUnavailable';
  /** The round that was asked for, so the copy can name it. `null` when even that is unknown. */
  round: number | null;
}

export type ReviewSubject = GradedReviewSubject | GradedSubjectUnavailable;

/** The graded review's route-persisted state. This is UI state only; it never becomes a
 * playback-coordination request or a shared presenter position. */
export interface GradedReviewViewerState {
  tick: number;
  zoom: number;
  playing: boolean;
  subjectColony: number;
}

export interface RoundsReviewProps extends Partial<RoundsReviewSeams> {
  /** Playback loop seams (the canvas render-loop precedent, design D8): injected so the
   * transport's advance is deterministically testable without a real
   * `requestAnimationFrame`. */
  scheduler?: FrameScheduler;
  clock?: Clock;
  /**
   * **The route's round could not be resolved into something reviewable**
   * (`client-rounds-review` "The reviewed round is carried by the route, and is untrusted input").
   *
   * Present *instead of* a `subject`, never alongside one: its whole purpose is that no locator was
   * constructed, so the screen must not be able to render a trace and this refusal at the same time.
   * See {@link UnavailableRound} for why the three reasons are kept apart.
   */
  unavailableRound?: UnavailableRound;
  /**
   * Whether an `unauthorized` graded read means "this round has not been graded yet" rather than
   * "your credential was refused".
   *
   * Decided by the composition root, because the discrimination needs the tournament's phase and this
   * screen deliberately holds no tournament model of its own (`roundIsNotGradedYet`). Defaults to
   * `false`, which keeps an unexplained refusal reading as the refusal it is.
   */
  notGradedYet?: boolean;
}

/**
 * Why the round named by the route cannot be reviewed.
 *
 * Three reasons rather than one, because they mean different things to the player and only one of
 * them is transient:
 *
 *   * `total-unknown` — **pending**. Nothing is wrong with the round; the tournament's length has not
 *     arrived, so it cannot be validated yet. This is the state a *reload* lands in, and it is why
 *     the screen must offer a way out of it (see below).
 *   * `malformed` — the path segment is not an integer at all. A typo, a truncated share link.
 *   * `out-of-range` — an integer, but not a round this tournament has.
 *
 * The last two deliberately render **without** the tournament's round total beside them: pairing a
 * requested round with the real total would present `/review/999` as though it were round 999 of a
 * tournament that has 5.
 */
export interface UnavailableRound {
  reason: 'malformed' | 'out-of-range' | 'total-unknown';
  /** The raw segment, so the copy can name what was asked for without implying it was valid. */
  raw: string;
}

/** Milliseconds per second — the playback loop's tick conversion. */
const MS_PER_SECOND = 1000;

/**
 * This screen's own subject colony, until the graded consolidation (`rounds-client` task
 * group 10) reshapes {@link RoundsReviewSeams} to carry a real `ReviewSubject`. Every run
 * this screen opens today is a **sandbox** run — the player's colony alone on the full field
 * (ROUNDS-MODE **G3**) — so there is exactly one colony to watch, and it is always colony 0.
 * Named rather than inlined so the eventual replacement is a one-line, greppable change
 * rather than a scattering of bare `0`s.
 */
const SANDBOX_SUBJECT_COLONY = 0;

/** The review camera's zoom range. The floor sits below the whole-field establishing
 * scale (0.2 shows a 4096 u field in ~820 px) so a player can always pull back to the
 * match view; the ceiling matches the Match screen's. */
const REVIEW_ZOOM_MIN = 0.08;
const REVIEW_ZOOM_MAX = 4;
const REVIEW_ZOOM_BOUNDS: CameraGestureBounds = {
  minScale: REVIEW_ZOOM_MIN,
  maxScale: REVIEW_ZOOM_MAX,
};

/** How far ahead of the playhead detail is prefetched, in seconds of REAL time at 1×
 * playback — the lead is multiplied by the playback speed, since speed is what sets how
 * fast windows are consumed. */
const PREFETCH_LEAD_SECONDS = 2;
/** A ceiling on prefetch requests per position, so a fast scrub cannot open an unbounded
 * burst of range fetches. */
const PREFETCH_WINDOWS_MAX = 8;

/** How many frames the screen keeps re-checking whether an outstanding detail range has
 * become resident before giving up. ~30 s at 60 Hz: far longer than any range fetch, and
 * bounded so a position with no detail at all cannot spin a frame loop for the session. */
const RESIDENCY_PROBE_FRAMES = 1800;

/** The pixel size used when the container has not been measured (jsdom, or the first
 * paint before the ref resolves) — the nominal Stage-3 viewport, so a camera derived
 * before layout is still centred rather than NaN. */
const FALLBACK_VIEWPORT = { width: 402, height: 874 } as const;

function measuredSize(el: HTMLElement | null): { width: number; height: number } | null {
  if (el === null) {
    return null;
  }
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0 ? { width: rect.width, height: rect.height } : null;
}

export function RoundsReview({
  runs = [],
  // No inert-default reader: see `rounds-review-source.ts`. Without one the screen opens
  // nothing and falls through to its empty state, which is what "inert" honestly means
  // for a seam whose method must return a value.
  reader,
  initialRunId,
  createRenderer,
  subject,
  colonies = [],
  phase,
  onOpenStandings,
  viewerState,
  onViewerStateChange,
  scheduler = rafFrameScheduler,
  clock = performanceClock,
  spectate = false,
  unavailableRound,
  notGradedYet = false,
}: RoundsReviewProps = {}) {
  const graded = subject?.mode === 'graded';
  /** A graded review with no subject to open. Scalar, so the effects below can gate on it without
   * an object identity in their dependency lists. */
  const subjectUnavailable = subject?.mode === 'gradedUnavailable';
  const viewerStateRef = useRef(viewerState);
  viewerStateRef.current = viewerState;
  /**
   * The snapshot object this screen most recently *reported*.
   *
   * `viewerState` is a **restore input supplied from outside**, not a controlled value: the parent
   * stores what the screen reports and hands the same object straight back, so without this the
   * screen consumes its own echo. That was a real freeze, not a theoretical one — the report effect
   * fires on mount, *before any trace has opened*, publishing the pre-open defaults
   * `{ tick: 0, playing: false }`; the open resolution then read them back over its own
   * `firstTick` / `playing: true` opening defaults, and the restore effect re-ran on every
   * subsequent report, rewinding the playhead to the last reported tick once per animation frame.
   * Graded review opened paused at 00:00 and never moved.
   *
   * Identity, not value: a value comparison cannot tell a restore apart from an echo (the parent's
   * snapshot is by construction value-equal to what the screen just held). A dropped echo never
   * reaches here at all — a parent that drops it leaves `viewerState`'s identity unchanged, so the
   * restore effect does not fire.
   *
   * **Every** self-report, not just the newest one: the echo is delivered by a parent re-render, so
   * it can land a commit or more after the report that produced it, by which time the playback loop
   * has already reported again. A single-slot ref stopped recognising the older snapshot as its own
   * and applied it as a restore — rewinding the playhead to the tick it carried (and, for the
   * pre-open snapshot, pausing). That is the same freeze this guard exists to prevent, reached by
   * timing rather than by ordering, and it is what made the loop test fail under CI load.
   */
  const selfReported = useRef(new WeakSet<GradedReviewViewerState>()).current;
  const [currentRunId] = useState<string | null>(
    initialRunId ?? runs[0]?.runId ?? null,
  );
  const [subjectColony, setSubjectColony] = useState(
    viewerState?.subjectColony ??
      (subject?.mode === 'graded' ? subject.subjectColony : undefined) ??
      SANDBOX_SUBJECT_COLONY,
  );
  /**
   * The spectate camera's anchor: a colony id once a rail row has been tapped, `null` for the
   * field-wide default. Deliberately **not** `subjectColony` — the per-colony readouts (score,
   * identity) may track the last-focused colony even after the presenter returns to the field,
   * while the *camera* must not: "back to the field" is meaningless if the next render just
   * re-anchors on whatever `subjectColony` still says.
   */
  const [spectateFocus, setSpectateFocus] = useState<number | null>(null);
  const [load, setLoad] = useState<TraceLoad | null>(null);
  const [position, setPosition] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<number>(DEFAULT_REVIEW_SPEED);
  const [dragging, setDragging] = useState(false);
  // --- the picked bee (WAG-36) ------------------------------------------------------------
  //
  // "Picked", never "selected": `client-rounds-selection` is an existing capability about choosing
  // a candidate program set, and blurring the two would be a naming hazard on this very screen
  // (design **E9**). The pick is deliberately NOT part of `GradedReviewViewerState` — it is
  // per-viewing inspection, not the private playback state standings must preserve — and it is
  // deliberately independent of the subject colony, since reading a rival's bee is the point.
  const [pickedBeeId, setPickedBeeId] = useState<number | null>(null);
  /** Bumped on every discontinuous playhead jump, so the label layer re-baselines silently rather
   * than flashing every transition the jump skipped over. */
  const [seekNonce, setSeekNonce] = useState(0);
  function seekTo(tick: number): void {
    setPosition(tick);
    setSeekNonce((n) => n + 1);
  }

  // A corruption discovered AFTER the trace opened (implementation-review **F9**): detail
  // arrives by range, so a damaged detail tier is learned about while the screen is already
  // rendering coarse. Coarse is a legitimate view, so leaving it up would make a corrupt
  // trace indistinguishable from a healthy wide-zoom one — the exact failure the fail-closed
  // rule (**D4**, **D6**) exists to prevent. Held as its own state so the trace stops being
  // rendered the moment it is reported.
  const [lateCorruption, setLateCorruption] = useState<TraceCorruptionReport | null>(null);

  // The controlled review camera (`{centre, zoom}`, the Match/Colony convention) — the
  // screen's own state, never shared and never host-driven (UI.md §6: "position, zoom and
  // play state are private to the viewer"). It opens on the COLONY view: centred on the
  // run's hive at `COLONY_CLOSE_SCALE`, because a sandbox run is one colony on a
  // 4096 u field and the whole-field framing this screen used to hard-code put that
  // colony in a corner as an unreadable speck.
  const [cameraView, setCameraView] = useState<{ centre: [number, number]; zoom: number }>({
    centre: [0, 0],
    zoom: viewerState?.zoom ?? COLONY_CLOSE_SCALE,
  });
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const [viewport, setViewport] = useState<{ width: number; height: number }>(FALLBACK_VIEWPORT);

  const run = runs.find((r) => r.runId === currentRunId) ?? null;
  const watchedColony = graded ? subjectColony : SANDBOX_SUBJECT_COLONY;
  const loadedTrace = load?.kind === 'loaded' ? load.trace : null;
  // The trace the screen renders: nothing, once any corruption is known.
  const trace = lateCorruption === null ? loadedTrace : null;
  const corruption: TraceCorruptionReport | null =
    lateCorruption ??
    (load?.kind === 'corrupt' ? { component: load.component, message: load.message } : null);
  /**
   * A trace this build no longer reads — **stale, not damaged**.
   *
   * `TraceLoad` grew its `unsupported` arm for device-retained practice traces (`bee-state-flash`
   * task 4.5) and this screen branched only on `corrupt`, so the new arm rendered a blank screen
   * with no message at all: no trace, no transport, no alert. Unreachable through today's graded
   * route — a graded trace is served by the current build, and `rounds-http` maps a stale manifest
   * to `corrupt` — but a silent-blank-screen branch is not something to leave standing one screen
   * over from the one this change fixed. The Sandbox screen's own copy
   * (`sandbox-coordinator.ts`'s `traceStaleFormatMessage`) makes the same distinction for the same
   * reason: a damaged trace means something went wrong and is worth reporting, a stale one means
   * only that the app was updated since the run was written.
   */
  const staleFormat = load?.kind === 'unsupported' ? load : null;

  // Subscribe to the loaded trace's own corruption channel. Re-subscribing per trace also
  // clears a previous run's report, so switching runs on the rail is a clean slate.
  useEffect(() => {
    setLateCorruption(null);
    if (loadedTrace?.onCorrupt === undefined) {
      return undefined;
    }
    return loadedTrace.onCorrupt(setLateCorruption);
  }, [loadedTrace]);

  // A new route subject is a new graded round. A rail/chip selection is intentionally NOT a prop
  // update, so it stays private to this viewer and does not trigger the opening effect below.
  useEffect(() => {
    if (graded) {
      setSubjectColony(viewerStateRef.current?.subjectColony ?? subject.subjectColony);
    }
  }, [
    graded,
    graded ? subject.locator.round : null,
    graded ? subject.locator.colony : null,
    graded ? subject.subjectColony : null,
  ]);

  // A parent may restore the snapshot either before or after the async trace open resolves.
  // Applying it independently makes both orderings correct without a second trace open.
  useEffect(() => {
    if (!graded || viewerState === undefined || selfReported.has(viewerState)) {
      return;
    }
    setPosition(viewerState.tick);
    setPlaying(viewerState.playing);
    setSubjectColony(viewerState.subjectColony);
    setCameraView((current) => ({
      centre: current.centre,
      zoom: Math.min(REVIEW_ZOOM_MAX, Math.max(REVIEW_ZOOM_MIN, viewerState.zoom)),
    }));
  }, [graded, viewerState]);

  // Open the selected run's trace. A late resolution for a run the player has already
  // moved off is discarded, so a slow open can never clobber a newer selection.
  const openTokenRef = useRef(0);
  useEffect(() => {
    // `subjectUnavailable` is checked here and not only at render: a graded round with no subject
    // must open **nothing**, and without this guard it would fall through the `!graded` arm below
    // and open one of the caller's own sandbox runs in the graded round's place (**F4**).
    if (reader === undefined || subjectUnavailable || (!graded && currentRunId === null)) {
      setLoad(null);
      return;
    }
    const token = ++openTokenRef.current;
    setLoad(null);
    const locator: TraceLocator = graded
      ? subject.locator
      : { kind: 'sandboxRun', runId: Number(currentRunId) };
    void reader.open(locator).then((result) => {
      if (openTokenRef.current !== token) {
        return;
      }
      setLoad(result);
      if (result.kind === 'loaded') {
        // The screen's own echo is not a restore — see `selfReportedRef`. Without this the
        // pre-open report replaces the opening defaults with the very state that produced it.
        const restored =
          graded && viewerStateRef.current !== undefined && !selfReported.has(viewerStateRef.current)
            ? viewerStateRef.current
            : undefined;
        setPosition(restored?.tick ?? result.trace.firstTick);
        setPlaying(restored?.playing ?? true);
      }
    });
  }, [currentRunId, reader, graded, subjectUnavailable, graded ? subject.locator : null]);

  // The playback loop. Held while dragging, so a scrub is not fought by the clock; the
  // effect re-arms from `clock.now()` on resume, so a pause never accrues a time debt
  // and playback continues from the tick it was held at.
  useEffect(() => {
    if (!playing || dragging || trace === null) {
      return undefined;
    }
    let cancelled = false;
    let handle = 0;
    let last = clock.now();
    const frame = () => {
      if (cancelled) {
        return;
      }
      const now = clock.now();
      const deltaMs = Math.max(0, now - last);
      last = now;
      setPosition((current) =>
        Math.min(trace.terminalTick, current + (deltaMs / MS_PER_SECOND) * trace.tickRateHz * speed),
      );
      handle = scheduler.schedule(frame);
    };
    handle = scheduler.schedule(frame);
    return () => {
      cancelled = true;
      scheduler.cancel(handle);
    };
  }, [playing, dragging, speed, trace, scheduler, clock]);

  const tick = trace === null ? 0 : Math.floor(position);
  const detailResident = trace !== null && trace.detailAt(tick) !== null;

  // --- the spectate readouts (design **D10**) -----------------------------------------
  //
  // A lookup in the coarse tier's nearest sample at or before the playhead — nothing here
  // scans the detail tier or opens a second read.
  // Read for **every** graded review, not only spectate: the identity line's "how many colonies
  // are in this round" is the same question, and answering it from the roster instead was how a
  // player's device announced a 150-colony battle royale as "3 colonies" (E2E round 2, **N5**).
  // A coarse lookup costs nothing — the tier is already downloaded whole.
  const coarseSample = trace !== null ? trace.coarseAt(tick) : null;
  const spectateRowCap = 15;
  const railColonies = spectate ? spectateRailColonies(coarseSample, colonies) : colonies;
  const eliminatedCount = spectate ? eliminatedCountAt(coarseSample) : 0;
  // Looked up in the **rail's** roster, not the standings' one. In spectate the rail is the
  // standings roster plus a row per colony the trace carries that it does not — the bots — so a
  // presenter clicking a bot row used to land on a header reading "Unknown colony" beside a rail
  // row labelled "Bot" (E2E round 2, N9). Outside spectate the two rosters are the same object,
  // so this is the same lookup it always was.
  const gradedColony = graded ? railColonies.find((entry) => entry.colony === watchedColony) ?? null : null;

  // Ask for the range covering the current position whenever it is not resident, **and
  // prefetch the ranges the playhead is about to reach.**
  //
  // The prefetch is the fix for a real defect, not an optimisation. Detail is fetched one
  // ~1-sim-second window at a time, on demand, and only for the window containing the
  // current tick — so at 1× playback the playhead crossed a window boundary every second
  // and *raced an HTTP round trip every time*. Each lost race dropped a frame (often
  // several) to the coarse tier, which the player saw as the field strobing once a second:
  // the bees vanishing and the wide tier's orange swarm markers flashing up in their place.
  // Requesting ahead of the playhead means the next window is already resident when the
  // clock reaches it, so the common case has no gap at all.
  //
  // The lead scales with playback speed, because that is what sets how fast windows are
  // consumed: at 16× the playhead eats sixteen windows a second. It is capped so a fast
  // scrub cannot open an unbounded burst of requests, and stepped by `tickRateHz` (one
  // sim-second, the reader's window size) — a step that lands twice inside one window is
  // free, since `requestDetail` is idempotent per window.
  useEffect(() => {
    if (trace === null) {
      return;
    }
    if (!detailResident) {
      trace.requestDetail(tick);
    }
    if (!playing) {
      return;
    }
    const step = Math.max(1, Math.round(trace.tickRateHz));
    const windows = Math.min(PREFETCH_WINDOWS_MAX, Math.ceil(PREFETCH_LEAD_SECONDS * speed));
    for (let i = 1; i <= windows; i += 1) {
      const ahead = tick + i * step;
      if (ahead > trace.terminalTick) {
        break;
      }
      trace.requestDetail(ahead);
    }
  }, [trace, tick, detailResident, playing, speed]);

  // Re-check residency while a range is outstanding.
  //
  // `TraceReader` is deliberately fire-and-forget: `requestDetail` fills the trace's own
  // residency map and has no way to tell React about it. During playback that is invisible,
  // because the transport re-renders every frame and re-asks `detailAt`. **Paused, it is
  // not**: a player who scrubs and waits sat on coarse forever, even seconds after the bytes
  // had landed — the range is fetched, decoded and resident, and the screen never looks
  // again. That reads as a broken trace rather than a paused one, and it is exactly the
  // state a player scrubbing for diagnosis is in.
  //
  // A bounded poll on the injected frame scheduler, rather than a new notification on the
  // seam: the screen already runs this scheduler for playback, so nothing new is introduced
  // and the poll is deterministic under `FakeFrameScheduler`. It stops the moment the range
  // lands, and gives up after `RESIDENCY_PROBE_FRAMES` so a genuinely absent range (the
  // position falls outside the detail span) cannot spin a loop forever.
  const [, setResidencyProbe] = useState(0);
  useEffect(() => {
    if (trace === null || detailResident) {
      return undefined;
    }
    let cancelled = false;
    let frames = 0;
    let handle = 0;
    const probe = () => {
      if (cancelled || frames >= RESIDENCY_PROBE_FRAMES) {
        return;
      }
      frames += 1;
      setResidencyProbe((n) => n + 1);
      handle = scheduler.schedule(probe);
    };
    handle = scheduler.schedule(probe);
    return () => {
      cancelled = true;
      scheduler.cancel(handle);
    };
  }, [trace, tick, detailResident, scheduler]);

  // **Hold the last detail frame across a short gap**, rather than dropping to coarse for
  // the one or two frames a lost prefetch race costs.
  //
  // The prefetch above removes nearly every gap; this removes the flicker from the ones it
  // cannot (a cold seek, a slow response, a 16× sweep outrunning the network). Falling to
  // the wide tier for ~100 ms and back is not "showing coarse" in the sense UI.md means —
  // it is a strobe, and a strobing field is unreadable in exactly the moments a player is
  // trying to read it.
  //
  // What is shown during the hold is a **real detail frame**, just a beat old — the video
  // player's rule, not a placeholder and not an invention. The frame reports its own tick,
  // so nothing claims the stale frame is the current one, and the hold is bounded at
  // `detailHoldTicks(trace.tickRateHz)` (tick-rate-single-source task 6.2 — derived from the
  // trace's own rate, never a bare 15): past that the gap is genuine and the screen falls back
  // to coarse exactly as the spec requires, tier indicator and all.
  const lastResidentTickRef = useRef<number | null>(null);
  useEffect(() => {
    if (detailResident) {
      lastResidentTickRef.current = tick;
    }
  }, [detailResident, tick]);
  // A run switch invalidates the previous run's held frame outright.
  useEffect(() => {
    lastResidentTickRef.current = null;
  }, [currentRunId, watchedColony]);

  const heldTick = lastResidentTickRef.current;
  const holding =
    !detailResident &&
    trace !== null &&
    heldTick !== null &&
    Math.abs(tick - heldTick) <= detailHoldTicks(trace.tickRateHz) &&
    trace.detailAt(heldTick) !== null;
  /** The tick the FRAME draws: the playhead, or the held frame's own tick during a gap. */
  const frameTick = holding ? heldTick! : tick;
  /** Whether what is on screen is a detail frame — held or live. The tier indicator reports
   * this rather than raw residency, so it describes the picture, never a different one. */
  const showingDetail = detailResident || holding;
  /** Whether the current tick falls inside a reclaimed span — the same signal the track's
   * hatched marks read, so the indicator and the track cannot disagree
   * (`rounds-trace-retention`). */
  const detailEvicted = trace !== null && tickInsideSpans(tick, trace.evictedSpans);

  // --- camera ------------------------------------------------------------------------

  // Spectate's own default is the **field**, not a colony (design **D12**): `spectateFocus`
  // is `null` until a rail row is tapped, and every reading below falls back to the field
  // centre — `[0, 0]`, `dequantizePosition`'s own origin — exactly the fallback the ordinary
  // colony framing already uses while a hive's first detail range has not arrived yet.
  const spectateFieldMode = spectate && spectateFocus === null;
  const cameraColony = spectate ? (spectateFocus ?? watchedColony) : watchedColony;
  // Recentre on the colony whenever a trace opens. The anchor is the hive-cell centroid
  // at the trace's first tick (`hiveCentreOf`) — a colony's cells are the only fixed
  // marker of where it lives — falling back to the field centre when the first tick's
  // detail range has not arrived yet, in which case the next effect run (once it has)
  // moves the camera onto the hive.
  const homeCentre: [number, number] =
    trace === null || spectateFieldMode
      ? [0, 0]
      : (hiveCentreOf(trace, trace.firstTick, cameraColony) ?? [0, 0]);
  // The anchor the frame draws the hive tile and the own queen at: this tick's comb
  // centroid where the range is resident, else the opening one. Not `homeCentre` alone —
  // the comb grows through a run as builders add cells, and pinning the tile to the
  // tick-0 centroid would slide it out from under its own cells.
  const hiveAnchor: [number, number] | null =
    trace === null ? null : (hiveCentreOf(trace, tick, watchedColony) ?? homeCentre);
  // The opening ZOOM, derived from the watched colony rather than declared — the same rule
  // Sandbox uses (`sandbox-camera-colony-framing`): the shot at which that colony's own hive
  // cells, padded, fit the measured container. `COLONY_CLOSE_SCALE` keeps its live-mode meaning
  // as the Colony screen's calibrated display constant and is what is left when there is no
  // colony to fit — no detail yet, no cells, or a degenerate extent.
  //
  // This screen used to pin the opening zoom to that constant outright, which put graded review
  // at 1.0 against the state-label layer's 2.0 floor (`canvas/state-labels.ts`) — so the labels
  // were **off at the opening shot** on the one screen whose whole purpose is reading a rival's
  // behaviour, and a player had to zoom in before the feature existed for them. Deriving the fit
  // here rather than lowering that floor is deliberate: the floor is a legibility knee, so
  // lowering it would trade an invisible feature for an unreadable one (`bee-state-flash` E7).
  const homeZoom = useMemo(() => {
    if (trace === null) {
      return COLONY_CLOSE_SCALE;
    }
    if (spectateFieldMode) {
      return (
        fieldFitZoom(trace, viewport, {
          minScale: REVIEW_ZOOM_MIN,
          maxScale: REVIEW_ZOOM_MAX,
        }) ?? COLONY_CLOSE_SCALE
      );
    }
    return (
      colonyFitZoom(trace, trace.firstTick, cameraColony, viewport, {
        minScale: REVIEW_ZOOM_MIN,
        maxScale: REVIEW_ZOOM_MAX,
      }) ?? COLONY_CLOSE_SCALE
    );
    // `homeCentre` is a dependency as well as the trace object because a trace's IDENTITY does
    // not change when its first window becomes resident: the same `ReviewTrace` starts answering
    // `detailAt` with a record it previously answered `null` for. Memoizing on the trace alone
    // would pin the fallback taken before any detail existed and never recompute.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trace, viewport, cameraColony, spectateFieldMode, homeCentre[0], homeCentre[1]]);
  const homeKey = `${graded ? `graded:${subject.locator.round}` : currentRunId}:${spectate ? `spectate:${spectateFocus ?? 'field'}` : watchedColony}:${homeCentre[0].toFixed(1)}:${homeCentre[1].toFixed(1)}`;
  const lastHomeRef = useRef<string | null>(null);
  useEffect(() => {
    if (trace === null || lastHomeRef.current === homeKey) {
      return;
    }
    lastHomeRef.current = homeKey;
    const restoredZoom =
      graded && viewerStateRef.current !== undefined && !selfReported.has(viewerStateRef.current)
        ? viewerStateRef.current.zoom
        : undefined;
    setCameraView({
      centre: homeCentre,
      zoom:
        restoredZoom === undefined
          ? homeZoom
          : Math.min(REVIEW_ZOOM_MAX, Math.max(REVIEW_ZOOM_MIN, restoredZoom)),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trace, homeKey, homeZoom]);

  // Track the canvas' measured size so culling and the centre inversion use real pixels.
  useEffect(() => {
    const el = canvasRef.current;
    if (el === null || typeof ResizeObserver === 'undefined') {
      const size = measuredSize(canvasRef.current);
      if (size !== null) {
        setViewport(size);
      }
      return undefined;
    }
    const observer = new ResizeObserver(() => {
      const size = measuredSize(el);
      if (size !== null) {
        setViewport((current) =>
          current.width === size.width && current.height === size.height ? current : size,
        );
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [run === null || reader === undefined]);

  const camera = cameraForZoomCentre(cameraView.centre, cameraView.zoom, viewport);

  function zoomBy(factor: number): void {
    setCameraView((current) => ({
      centre: current.centre,
      zoom: Math.min(REVIEW_ZOOM_MAX, Math.max(REVIEW_ZOOM_MIN, current.zoom * factor)),
    }));
  }

  function resetCamera(): void {
    // The same derived shot the screen opens at, not the bare constant — a recentre that landed
    // somewhere the opening never did would be a second, undocumented framing.
    setCameraView({ centre: homeCentre, zoom: homeZoom });
  }

  // Gesture handlers: a STABLE object whose methods are reassigned every render (the
  // Match screen's convention), so the hook's native listeners never tear down and
  // reattach on a camera-driven re-render.
  const gestureHandlersRef = useRef<CanvasCameraGestureHandlers>({
    onZoom: () => {},
    onPan: () => {},
  });
  gestureHandlersRef.current.onZoom = (screenPoint, factor) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (rect === undefined || rect.width <= 0 || rect.height <= 0) {
      zoomBy(factor);
      return;
    }
    const local = { x: screenPoint.x - rect.left, y: screenPoint.y - rect.top };
    const size = { width: rect.width, height: rect.height };
    const current = cameraForZoomCentre(cameraView.centre, cameraView.zoom, size);
    const next = zoomAboutScreenPoint(current, local, factor, REVIEW_ZOOM_BOUNDS);
    setCameraView({
      centre: screenToWorld(next, { x: size.width / 2, y: size.height / 2 }),
      zoom: next.scale,
    });
  };
  gestureHandlersRef.current.onPan = (dxPx, dyPx) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (rect === undefined || rect.width <= 0 || rect.height <= 0) {
      return;
    }
    const size = { width: rect.width, height: rect.height };
    const panned = panByScreenDelta(
      cameraForZoomCentre(cameraView.centre, cameraView.zoom, size),
      dxPx,
      dyPx,
    );
    setCameraView({
      centre: screenToWorld(panned, { x: size.width / 2, y: size.height / 2 }),
      zoom: cameraView.zoom,
    });
  };
  // Tap to pick a bee, tap it again to clear (WAG-36). `canvas/pick.ts`'s inverse-tilt geometry,
  // unchanged — already written and tested for the live Colony screen, reused rather than
  // restated. Picking is an INSPECTION affordance, so it works under review-only transport: it
  // does not consult `playing`, and the frame it hit-tests is the one on screen.
  gestureHandlersRef.current.onTap = (screenPoint) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (rect === undefined || rect.width <= 0 || rect.height <= 0 || trace === null) {
      return; // Unmeasured container: no jsdom safety net for tap pixel geometry (D-086).
    }
    const world = reviewWorldSnapshot(trace, frameTick);
    if (world === null) {
      return;
    }
    const size = { width: rect.width, height: rect.height };
    const picked = pickEntityAt(
      { x: screenPoint.x - rect.left, y: screenPoint.y - rect.top },
      cameraForZoomCentre(cameraView.centre, cameraView.zoom, size),
      world.bees.values(),
    );
    // A tap that hits nothing leaves the pick alone (the Colony screen's rule): over a busy field
    // a miss is far likelier than a deliberate clear, which is what the chip is for.
    if (picked === null) {
      return;
    }
    setPickedBeeId((current) => (current === picked ? null : picked));
  };
  useCanvasCameraGestures(canvasRef, gestureHandlersRef.current, true);

  // A different trace is a different world; the picked id means nothing in it. A **subject
  // switch** deliberately does not appear here — same trace, same bees, and retaining the pick
  // across it is the point of the subject exemption.
  useEffect(() => {
    setPickedBeeId(null);
  }, [loadedTrace]);

  // A one-way local snapshot. The callback is deliberately not a transport seam: route state
  // can preserve this viewer across `/standings`, but no other viewer can observe it.
  useEffect(() => {
    if (!graded || onViewerStateChange === undefined) {
      return;
    }
    const reported: GradedReviewViewerState = {
      tick: position,
      zoom: cameraView.zoom,
      playing,
      subjectColony,
    };
    selfReported.add(reported);
    onViewerStateChange(reported);
  }, [graded, onViewerStateChange, position, cameraView.zoom, playing, subjectColony]);

  // --- inert / empty --------------------------------------------------------------

  /**
   * **A way out, offered in every state** (`client-rounds-review` "The review route offers a way out
   * before its seams have arrived").
   *
   * The pending and empty states below are reached with **no injected seams at all** — which is
   * exactly the situation a reload onto this route creates — and that is precisely when the screen's
   * ordinary standings affordance is absent. Before this, a reload onto `/review/2` rendered a screen
   * with no control on it: a pending state that traps the player is worse than a failure state that
   * explains itself.
   */
  const wayOut =
    onOpenStandings === undefined ? null : (
      <button
        type="button"
        data-testid="rounds-review-leave"
        onClick={onOpenStandings}
        style={WAY_OUT_STYLE}
      >
        Back to standings
      </button>
    );

  /**
   * The route named a round this screen cannot open.
   *
   * Checked **first**, before every other state: no locator was built, so there is nothing to open and
   * no run list to fall back to. Falling through to the empty state instead would say "nothing to
   * review yet — finish a run", which is false and misleading for a mistyped or stale link.
   */
  if (unavailableRound !== undefined) {
    const pending = unavailableRound.reason === 'total-unknown';
    return (
      <div data-testid="rounds-review-screen" style={SCREEN_STYLE}>
        <div
          data-testid={pending ? 'rounds-review-pending' : 'rounds-review-unavailable-round'}
          role="status"
          style={UNREADABLE_STYLE}
        >
          <span style={{ display: 'block' }}>
            {pending
              ? 'Finding that round…'
              : // Deliberately **no round-of-total readout**: pairing what was asked for with the
                // tournament's real total would present an out-of-range round as though it were one.
                'That round isn’t part of this tournament.'}
          </span>
          {wayOut}
        </div>
      </div>
    );
  }

  // A graded round the caller has no colony for. **Named**, and checked before the empty state
  // below: "nothing to review yet" would be false — the round exists and is graded, what is
  // missing is which colony in it is theirs. Reuses the `graded-review-unreadable` treatment,
  // because this is the same class of state (the trace is there; the caller cannot be placed in
  // it) and inventing a second visual language for it would be worse than sharing one.
  if (subjectUnavailable) {
    return (
      <div data-testid="rounds-review-screen" style={SCREEN_STYLE}>
        <div data-testid="graded-review-no-colony" role="alert" style={UNREADABLE_STYLE}>
          <span style={{ display: 'block' }}>
            {subject.round === null
              ? 'This graded round can’t be opened — your colony in it isn’t known yet.'
              : `Round ${subject.round} can’t be opened — your colony in it isn’t known yet.`}
          </span>
          {wayOut}
        </div>
      </div>
    );
  }

  if ((!graded && run === null) || reader === undefined) {
    return (
      <div data-testid="rounds-review-screen" style={SCREEN_STYLE}>
        <div
          data-testid="rounds-review-empty"
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            textAlign: 'center',
            color: 'var(--hud-text-dim)',
            fontFamily: 'var(--font-display)',
          }}
        >
          <span style={{ display: 'block' }}>
            Nothing to review yet — finish a run from your colony, then choose Review.
          </span>
          {wayOut}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="rounds-review-screen" style={SCREEN_STYLE}>
      {/* The field frame — one tick of the trace, at whichever tier is available. */}
      <div
        ref={canvasRef}
        data-testid="review-canvas"
        style={{
          position: 'absolute',
          top: graded ? 88 : 0,
          right: 0,
          bottom: graded ? 64 : 0,
          left: 0,
          background: 'var(--field-bg, #7FA65B)',
          // Browser page-zoom/scroll suppression over the field is unconditional, the
          // same rule the Match and Colony canvases follow (UI.md §5).
          touchAction: 'none',
          cursor: 'grab',
        }}
      >
        {trace !== null && (
          <ReviewFrame
            trace={trace}
            tick={frameTick}
            camera={camera}
            viewport={viewport}
            hive={hiveAnchor}
            subjectColony={watchedColony}
            // WAG-36. Here the subject gate does real work: every colony in a graded round is
            // scripted, so without it a late-game field is a wall of overlapping text. The pick
            // is the deliberate, precise exemption from it.
            scrubbing={dragging}
            seekNonce={seekNonce}
            pickedBeeId={pickedBeeId}
            onPickedBeeChange={setPickedBeeId}
            createRenderer={createRenderer}
          />
        )}
      </div>

      {/* The way out of a pick (WAG-36 task 9.4). Above the colony chip bar and clear of the
          standings control — an absolutely-positioned overlay, so no screen height moves. */}
      {trace !== null && pickedBeeId !== null && (
        <PickedBeeChip
          beeId={pickedBeeId}
          onClear={() => setPickedBeeId(null)}
          testId="review-clear-pick"
          // Graded clears the colony chip bar (bottom 72); the sandbox-run mode only has to clear
          // the transport stack, which tops out around 76.
          style={{ bottom: graded ? 112 : 88, left: '50%', transform: 'translateX(-50%)' }}
        />
      )}

      {trace !== null && <ReviewCameraControl zoom={cameraView.zoom} onReset={resetCamera} />}

      {graded ? (
        <>
          <div data-testid="graded-review-identity" style={{ position: 'absolute', top: 12, left: 12, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <div style={IDENTITY_ROW}>
              <b style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 13, color: 'var(--hud-text)' }}>Graded round</b>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--hud-text-dim)' }}>
                Round {subject.locator.round} of {subject.roundsTotal ?? phase?.roundsTotal ?? '—'} · Colony {watchedColony} · {gradedColony?.displayName ?? (gradedColony?.isBot === true ? 'Bot' : 'Unknown colony')}
              </span>
            </div>
            <div style={IDENTITY_ROW}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--hud-text-dim)' }}>
                {/* UI/UX audit F-16: "1 colonies". `review-model.ts` already pluralises this
                    correctly for the run summary; this second site hardcoded the plural. */}
                {(() => {
                  // Spectate counts the **field**, which is what the rail now lists: the
                  // standings-derived roster carries only the colonies that score, so a
                  // bot-filled round read "1 colony" beside a six-row rail and a climbing
                  // eliminated counter (found by the task-11.6 rehearsal).
                  // **The trace's own field first** (E2E round 2, N5). The player-facing arm read
                  // `colonies.length` — the standings-derived roster, which carries only the
                  // colonies that score — so a phone watching the 150-colony graded round
                  // announced it as "3 colonies", the number of humans in the room. The roster
                  // stays as the last resort for a trace with no sample yet.
                  const n =
                    coarseSample?.colonies.length ??
                    subject.colonyCount ??
                    (spectate ? railColonies.length : colonies.length);
                  return `${n} ${n === 1 ? 'colony' : 'colonies'}`;
                })()}{' '}
                {/* **No `run?.seed` fallback.** `run` is one of the *caller's own sandbox runs*,
                    which is a different match entirely: the graded review read "seed 2" — the
                    seed of the player's own solo rehearsal — over a graded round seeded
                    elsewhere. A seed the subject does not carry is not knowable here, and an
                    em dash says so (the presenter's spectate has always shown one). */}
                · seed {subject.seed ?? '—'} · {trace === null ? '—' : clockLabel(trace.terminalTick, trace.tickRateHz)}
              </span>
            </div>
          </div>
          {phase !== undefined && <PhaseClock snapshot={phase} style={{ position: 'absolute', top: 12, right: 12, zIndex: 2 }} />}
          {onOpenStandings !== undefined && (
            <button
              type="button"
              data-testid="review-open-standings"
              onClick={onOpenStandings}
              style={{ position: 'absolute', right: 12, bottom: 12, minHeight: 44, padding: '0 14px', border: '1.5px solid var(--hud-line)', borderRadius: 12, background: 'var(--hud-bg)', color: 'var(--hud-text)', boxShadow: 'var(--shadow-hud)', fontFamily: 'var(--font-display)', fontWeight: 600, cursor: 'pointer' }}
            >
              Standings
            </button>
          )}
          {trace !== null && !spectate && gradedColony !== null && <GradedScoreReadout colony={gradedColony} />}
          {trace !== null && (
            <ColonyRail
              colonies={railColonies}
              subjectColony={watchedColony}
              onSelect={(colony) => {
                setSubjectColony(colony);
                if (spectate) {
                  setSpectateFocus(colony);
                }
              }}
              rowCap={spectate ? spectateRowCap : undefined}
              orderBy={spectate ? 'score' : 'placement'}
            />
          )}
          {trace !== null && !spectate && (
            <ColonyChipBar colonies={colonies} subjectColony={watchedColony} onSelect={setSubjectColony} />
          )}
          {/* The spectate-only readouts (design **D10**): an eliminated counter tracking the
              playhead, and the back-to-the-field control — both operate on the already-open
              trace and issue no request of their own. */}
          {spectate && trace !== null && (
            <div
              data-testid="spectate-eliminated-count"
              // **Below the phase clock, not under it.** Both wanted `top: 12; right: 12`, and the
              // clock carries `zIndex: 2` — so on the presenter's own composition, which always
              // passes a phase, the clock covered the counter outright and the "players wiped out"
              // beat had nothing on screen. Offset by the clock's own row when there is one; the
              // corner is still the counter's when there is not.
              style={{ position: 'absolute', top: phase === undefined ? 12 : 56, right: 12, zIndex: 2, minHeight: 34, padding: '0 12px', display: 'flex', alignItems: 'center', border: '1.5px solid var(--hud-line)', borderRadius: 'var(--r-pill)', background: 'var(--hud-bg)', color: 'var(--hud-text)', fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 12 }}
            >
              {eliminatedCount} eliminated
            </div>
          )}
          {spectate && trace !== null && spectateFocus !== null && (
            <button
              type="button"
              data-testid="spectate-back-to-field"
              onClick={() => setSpectateFocus(null)}
              style={{ position: 'absolute', left: 12, bottom: 12, minHeight: 44, padding: '0 14px', border: '1.5px solid var(--hud-line)', borderRadius: 12, background: 'var(--hud-bg)', color: 'var(--hud-text)', boxShadow: 'var(--shadow-hud)', fontFamily: 'var(--font-display)', fontWeight: 600, cursor: 'pointer' }}
            >
              Back to the field
            </button>
          )}
        </>
      ) : (
        <>
          <div
            data-testid="review-identity"
            style={{ position: 'absolute', top: 12, left: 12, display: 'flex', flexDirection: 'column', gap: 3 }}
          >
            <div style={IDENTITY_ROW}>
              <b style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 13, color: 'var(--hud-text)' }}>Reviewing</b>
              <span style={KIND_BADGE_STYLE}>{runKindLabel(run!.kind)}</span>
            </div>
            <div style={IDENTITY_ROW}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--hud-text-dim)' }}>{runIdentityLine(run!)}</span>
            </div>
          </div>
          <ScoreReadout run={run!} />
        </>
      )}

      {/* A corrupt trace is a REAL failure, not tier degradation. In this change absent
          declared bytes are never intentional (eviction moved to `rounds-trace-http`,
          artifacts-review F6), so it must never be dressed up as ordinary coarse — and
          nothing pretends to be scrubbable. */}
      {/* **Two different things wear one refusal** (`client-rounds-review` "A round that exists but
          is not yet graded is named as such"). `rounds-tournament-trace-read` serves a graded round
          only once it is graded and complete, and refuses an ungraded one `unauthorized` — the same
          word it uses for a credential failure. The round became bookmarkable in this change, so a
          player returning to a saved round during the next round's editing phase hits this constantly,
          and telling them their membership failed is both false and alarming: nothing is wrong with
          their session and there is nothing for them to fix. `notGradedYet` is decided by the root,
          which knows where the tournament is; a genuine refusal still reads exactly as it did. */}
      {graded && corruption?.message === 'unauthorized' && notGradedYet ? (
        <div data-testid="graded-review-not-graded" role="status" style={UNREADABLE_STYLE}>
          <span style={{ display: 'block' }}>
            That round hasn’t been graded yet — it’ll be here once its results are in.
          </span>
          {wayOut}
        </div>
      ) : graded && corruption?.message === 'unauthorized' ? (
        <div data-testid="graded-review-unreadable" role="alert" style={UNREADABLE_STYLE}>
          This graded round cannot be read with your tournament credential.
        </div>
      ) : corruption !== null && (
        <div
          data-testid="review-corrupt"
          role="alert"
          style={{
            position: 'absolute',
            bottom: 12,
            left: '50%',
            transform: 'translateX(-50%)',
            maxWidth: 'min(560px, calc(100% - 24px))',
            padding: '10px 14px',
            background: 'var(--danger-soft)',
            border: '1.5px solid var(--danger)',
            borderRadius: 12,
            boxShadow: 'var(--shadow-hud)',
            color: 'var(--ink-1)',
            fontSize: 13,
          }}
        >
          This {graded ? 'graded round’s' : 'run’s'} trace can’t be read — its <b>{corruption.component}</b> data is missing or
          damaged, so it can’t be replayed. ({corruption.message})
        </div>
      )}

      {/* Stale, not damaged: `role="status"`, not `alert`, and the neutral HUD chrome rather than
          the danger treatment — nothing has gone wrong. */}
      {staleFormat !== null && (
        <div
          data-testid="review-stale-format"
          role="status"
          style={{
            position: 'absolute',
            bottom: 12,
            left: '50%',
            transform: 'translateX(-50%)',
            maxWidth: 'min(560px, calc(100% - 24px))',
            padding: '10px 14px',
            background: 'var(--hud-bg)',
            border: '1.5px solid var(--hud-line)',
            borderRadius: 12,
            boxShadow: 'var(--shadow-hud)',
            color: 'var(--hud-text)',
            fontSize: 13,
          }}
        >
          This {graded ? 'graded round’s' : 'run’s'} trace was recorded by an older version of
          Waggle (format {staleFormat.formatVersion}; this build reads{' '}
          {staleFormat.supportedVersion}), so it can’t be replayed here. Nothing has gone wrong.
        </div>
      )}

      {trace !== null && (
        <SeekTransport
          firstTick={trace.firstTick}
          terminalTick={trace.terminalTick}
          tickRateHz={trace.tickRateHz}
          position={position}
          playing={playing}
          speed={speed}
          tier={tierStateFor(showingDetail, detailEvicted)}
          evictedSpans={trace.evictedSpans}
          onSeek={seekTo}
          onDragChange={setDragging}
          onTogglePlay={() => setPlaying((p) => !p)}
          onSpeed={setSpeed}
        />
      )}
    </div>
  );
}

const SCREEN_STYLE = { position: 'relative', width: '100%', height: '100%', overflow: 'hidden' } as const;

const IDENTITY_ROW = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 7,
  padding: '6px 12px',
  background: 'var(--hud-bg)',
  border: '1.5px solid var(--hud-line)',
  borderRadius: 12,
  boxShadow: 'var(--shadow-hud)',
} as const;

const KIND_BADGE_STYLE = {
  fontFamily: 'var(--font-display)',
  fontSize: 11,
  color: 'var(--accent-strong)',
  background: 'var(--accent-soft)',
  border: '1.5px solid var(--accent)',
  borderRadius: 999,
  padding: '2px 8px',
  textTransform: 'uppercase',
  letterSpacing: 'var(--tracking-caps, 0.08em)',
} as const;

/** The escape hatch on every dead-endable state. 44px tall, so it is as tappable as any other
 * control (UI.md's hit-target rule) rather than a courtesy link. */
const WAY_OUT_STYLE = {
  minHeight: 'var(--hit-target)',
  marginTop: 12,
  padding: '0 16px',
  borderRadius: 'var(--r-pill)',
  border: '1.5px solid var(--line-strong)',
  background: 'var(--surface-card)',
  color: 'var(--ink-1)',
  fontFamily: 'var(--font-display)',
  fontWeight: 500,
  fontSize: 14,
  cursor: 'pointer',
} as const;

const UNREADABLE_STYLE = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
  textAlign: 'center',
  background: 'var(--paper-1)',
  color: 'var(--hud-text)',
  fontFamily: 'var(--font-display)',
} as const;

function GradedScoreReadout({ colony }: { colony: GradedColony }) {
  const suffix = colony.placement % 100 >= 11 && colony.placement % 100 <= 13
    ? 'th'
    : ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[colony.placement % 10] ?? 'th';
  return (
    <div data-testid="review-score" style={{ position: 'absolute', top: 100, right: 12, width: 212, background: 'var(--hud-bg)', border: '1.5px solid var(--hud-line)', borderRadius: 16, boxShadow: 'var(--shadow-hud)', padding: '10px 12px' }}>
      <h4 style={{ margin: '0 0 8px', fontWeight: 800, fontSize: 9, textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps, 0.08em)', color: 'var(--hud-text-dim)' }}>Placement</h4>
      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 30, color: 'var(--hud-text)', lineHeight: 1 }}>
        {colony.placement}{suffix} place
      </div>
      <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--hud-line)', display: 'flex', justifyContent: 'space-between', gap: 8, color: 'var(--hud-text-dim)', fontFamily: 'var(--font-display)', fontSize: 12 }}>
        <span>G1 · bees alive at end</span><span style={{ color: 'var(--hud-text)', fontVariantNumeric: 'tabular-nums' }}>{colony.score}</span>
      </div>
    </div>
  );
}
